import { StackScreenProps } from "@react-navigation/stack";
import { GiftCardPaymentRootParamList } from "../../../../types";
import React from "react";
import {
  Button,
  Page,
  Stack,
  Text,
  TextField,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { useMutation } from "@apollo/client";
import { usePaymentControllerProvider } from "../../utils/payment-controller";
import { View } from "react-native-ui-lib";
import { useCheckoutContextProvider } from "../../utils/checkout";
import { moneyToFormattedString } from "@towersystems/roam-common/lib/shared-utils";
import { SpacingLiterals } from "react-native-ui-lib/src/style/spacings";
import { usePersistActiveSale } from "../../utils/sale-controller";
import { usePersistSale } from "../../../sales/utils";
import { MUTATION_REDEEM_GIFT_CARD } from "../../../../utilities/gift-card/constants";

export interface GiftCardPaymentScreenProps
  extends StackScreenProps<
    GiftCardPaymentRootParamList,
    "GiftCardPaymentScreen"
  > {}

export const GiftCardPaymentScreen = ({
  route,
  navigation,
}: GiftCardPaymentScreenProps) => {
  const persistActiveSale = usePersistActiveSale();
  const saveSale = usePersistSale();
  const { sale } = useCheckoutContextProvider();
  const [makingPayment, setMakingPayment] = React.useState(false);
  const flashMessagesContext = useFlashMessages();

  const { maximumTenderAmount, giftCardBalance, giftCardNumber } = route.params;

  const {
    tenderedAmountInput,
    tenderedAmountAsInteger,
    validTenderedAmount,
    setTenderedAmountInput,
  } = usePaymentControllerProvider();

  const [mutationRedeemGiftCard] = useMutation<{ redeemGiftCard: any }>(
    MUTATION_REDEEM_GIFT_CARD
  );

  React.useEffect(() => {
    setTenderedAmountInput(maximumTenderAmount.toString());
  }, []);

  const handleOnMakePayment = React.useCallback(async () => {
    setMakingPayment(true);
    if (!validTenderedAmount || !tenderedAmountAsInteger || makingPayment) {
      return;
    }

    // step1. MUST persist the sale on the server as gift card payments must be made online
    const sale = await persistActiveSale();
    if (!sale) {
      return;
    }

    try {
      const result = await mutationRedeemGiftCard({
        variables: {
          input: {
            saleId: sale._raw.id,
            amount: parseFloat(tenderedAmountInput),
            code: giftCardNumber,
          },
        },
      });
      if (result.data?.redeemGiftCard) {
        const updatedSale = await saveSale(result.data.redeemGiftCard, sale);
        if (!updatedSale) {
          return;
        }

        const paidOff = await updatedSale.balancePaidOff_ASYNC();

        if (paidOff) {
          //@ts-ignore
          navigation.navigate("CheckoutSummaryRootScreen", {
            screen: "CheckoutReviewScreen",
          });
        } else {
          //@ts-ignore
          navigation.navigate("SelectPaymentMethodScreen");
        }
      }
    } catch (err: any) {
      setMakingPayment(false);
      flashMessagesContext.showMessage({
        message: err?.message ?? "Could not redeem gift card",
        severity: "error",
      });
      console.log(err);
    }
    // step2. Make the gift card payment using the updated sale id
  }, [sale, validTenderedAmount, tenderedAmountAsInteger]);

  const isValidTenderedAmount = React.useMemo(() => {
    if (!validTenderedAmount || !tenderedAmountAsInteger) {
      return false;
    }

    const maximumTenderAmountAsInt = Math.round(maximumTenderAmount * 100);

    if (
      tenderedAmountAsInteger > maximumTenderAmountAsInt ||
      tenderedAmountAsInteger < 0
    ) {
      return false;
    }

    return validTenderedAmount;
  }, [maximumTenderAmount, validTenderedAmount, tenderedAmountAsInteger]);

  const footer = (
    <Button
      label={
        makingPayment ? "Processing payment..." : `Make payment with gift card`
      }
      loading={makingPayment}
      onPress={handleOnMakePayment}
      disabled={!isValidTenderedAmount || makingPayment}
      isAction
      fullWidth
    />
  );

  return (
    <Page
      title={`Gift card payment`}
      secondaryTitle={
        <View style={{ alignContent: "center", marginTop: SpacingLiterals.s2 }}>
          <Text
            style={{ textAlign: "center" }}
          >{`Gift card number: ${giftCardNumber}`}</Text>
          <Text
            style={{ textAlign: "center" }}
          >{`Balance remaining: ${moneyToFormattedString(
            giftCardBalance
          )}`}</Text>
        </View>
      }
      titleContainerStyle={{ alignItems: "center", justifyContent: "center" }}
      titleTextStyle={{ textAlign: "center" }}
      footer={footer}
    >
      <Stack vertical>
        <Text
          variant="heading4"
          fontWeight="bold"
          color="subdued"
          disabled={makingPayment}
        >
          Enter tendered amount
        </Text>

        <View flex>
          <TextField
            placeholder="Tendered amount"
            onChangeText={setTenderedAmountInput}
            value={tenderedAmountInput}
            formatterType={isValidTenderedAmount == true ? "money" : undefined}
            validationMessage={
              isValidTenderedAmount === false
                ? `Must be between 0.01 and ${moneyToFormattedString(
                    maximumTenderAmount
                  )}`
                : undefined
            }
          />
        </View>
      </Stack>
    </Page>
  );
};
